// The MIT License (MIT)
//
// Copyright (c) 2016-2023 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the 'Software'), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import angular from 'angular';
import gmfPermalinkShareService, {
    URL_MAX_LEN,
    URL_PATH_MAX_LEN,
} from 'gmf/permalink/ShareService';
import ngeoStatemanagerLocation from 'ngeo/statemanager/Location';
import { getUid as olUtilGetUid } from 'ol/util';

/**
 * @type {angular.IModule}
 * @hidden
 */

const myModule = angular.module('mapbsPermalinkShareComponent', [
    gmfPermalinkShareService.name,
    ngeoStatemanagerLocation.name,
]);

myModule.run(
    /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
    ($templateCache) => {
    // @ts-ignore: webpack
        $templateCache.put(
            'mapbs/shareComponent',
            require('./shareComponent.html')
        );
    }
);

myModule.value(
    'mapbsShareTemplateUrl',
    /**
   * @param {angular.IAttributes} $attrs Attributes.
   * @returns {string} The template url.
   */
    ($attrs) => {
        const templateUrl = $attrs.mapbsShareTemplateUrl;
        return templateUrl !== undefined ? templateUrl : 'mapbs/shareComponent';
    }
);

/**
 * @param {angular.IAttributes} $attrs Attributes.
 * @param {function(angular.IAttributes): string} mapbsShareTemplateUrl Template function.
 * @returns {string} Template URL.
 * @ngInject
 * @private
 * @hidden
 */
function gmfPermalinkShareTemplateUrl($attrs, mapbsShareTemplateUrl) {
    return mapbsShareTemplateUrl($attrs);
}

/**
 * Component to display a shortened permalink and share it by email Example:
 *
 *      <gmf-share></gmf-share>
 *
 * @type {angular.IComponentOptions}
 */
const mapbsShareComponent = {
    controller: 'MapbsShareController',
    templateUrl: gmfPermalinkShareTemplateUrl,
};
myModule.component('mapbsShare', mapbsShareComponent);

/**
 * @hidden
 */
export class MapbsShareController {
    /**
   * The controller for the share component
   *
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {import('ngeo/statemanager/Location').StatemanagerLocation} ngeoLocation ngeo Location service.
   * @param {import('gmf/permalink/ShareService').PermalinkShareService} gmfShareService service for
   *    sharing map.
   * @param {angular.IQService} $q Angular q service
   * @param {angular.IAttributes} $attrs Attributes.
   * @param {import('gmf/options').gmfShareOptions} gmfShareOptions The options.
   * @class
   * @ngInject
   * @ngdoc controller
   * @ngname GmfShareController
   */
    constructor(
        $scope,
        $injector,
        ngeoLocation,
        gmfShareService,
        $q,
        $attrs,
        gmfShareOptions
    ) {
        this.options = gmfShareOptions;

        /**
     * @type {string}
     */
        this.uid = olUtilGetUid(this);

        /**
     * @type {angular.IScope}
     * @private
     */
        this.$scope_ = $scope;

        /**
     * @param {angular.auto.IInjectorService}
     * @private
     */
        this.$injector = $injector;

        /**
     * @type {import('gmf/permalink/ShareService').PermalinkShareService}
     * @private
     */
        this.gmfShareService_ = gmfShareService;

        /**
     * @type {angular.IQService}
     * @private
     */
        this.$q_ = $q;

        /**
     * @type {import('ngeo/statemanager/Location').StatemanagerLocation}
     * @private
     */
        this.ngeoLocation_ = ngeoLocation;

        this.mapbsUrl = this.$injector.get('ngeoEgtBase');

        /**
     * @type {string}
     */
        this.shortLink = '';

        /**
     * @type {string}
     */
        this.email = '';

        /**
     * @type {string}
     */
        this.message = '';

        /**
     * @type {string}
     * @private
     */
        this.permalink_ = this.ngeoLocation_.getUriString();

        const path = ngeoLocation.getPath();
        if (!path) {
            throw new Error('Missing path');
        }
        /**
     * @type {boolean}
     */
        this.showLengthWarning =
      this.permalink_.length > URL_MAX_LEN || path.length > URL_PATH_MAX_LEN;

        /**
     * @type {boolean}
     */
        this.successfullySent = false;

        /**
     * @type {boolean}
     */
        this.errorOnsend = false;

        /**
     * @type {boolean}
     */
        this.isFinishedState = true;

        /**
     * @type {boolean}
     */
        this.errorOnGetShortUrl = false;

        // /**
        //  * @type {String}
        //  */
        // this.activePanel = 'nav-share';

        this.getShortUrl();

        const tabs = document.querySelectorAll('[role="tab"]');

        // Add a click event handler to each tab
        tabs.forEach((tab) => {
            tab.addEventListener('click', this.setPanels);
        });

        // default values of checkboxes
        this.config = {
            dataPanel: false,
            search: true,
            infoBar: true,
            addBtnShare: false,
            print: false
        };

        this.iFrameLink = this.getIFrameLink();

        document.getElementById('ngeo-modal-share').querySelectorAll('.modal-dialog')[0].classList.remove('modal-dialog-max-width');
    }

    /**
   * Get the short version of the permalink if the email is not provided
   */
    getShortUrl() {
        this.$q_.when(this.gmfShareService_.getShortUrl(this.permalink_)).then(
            (resp) => {
                this.shortLink =
                    /** @type {angular.IHttpResponse<{short_url: string}>} */ (
                        resp
                    ).data.short_url;
                this.errorOnGetShortUrl = false;
            },
            (resp) => {
                this.shortLink = this.permalink_;
                this.errorOnGetShortUrl = true;
            }
        );
    }

    getDisabledParam() {
        const disabledArr = [];
        // Add false value of config to disabled array
        if (!this.config.dataPanel) {
            disabledArr.push('dataPanel');
        }
        if (!this.config.search) {
            disabledArr.push('search');
        }
        if (!this.config.infoBar) {
            disabledArr.push('infoBar');
        }
        if (!this.config.addBtnShare) {
            disabledArr.push('addBtnShare');
        }
        if (!this.config.print) {
            disabledArr.push('print');
        }
        return disabledArr.join(',');
    }

    /**
   * Send the short version of the permalink if the email is provided
   */
    sendShortUrl() {
        this.errorOnsend = false;
        this.successfullySent = false;
        this.isFinishedState = false;

        // @ts-ignore: scope ......
        if (this.$scope_.gmfShareForm.$valid) {
            this.$q_
        .when(
            this.gmfShareService_.sendShortUrl(
                this.permalink_,
                this.email,
                this.message
            )
        )
        .then(
            (resp) => {
                this.successfullySent = true;
                this.isFinishedState = true;
            },
            (resp) => {
                this.errorOnsend = true;
                this.isFinishedState = true;
            }
        );
        }
    }

    getIFrameLink() {
        const location = this.ngeoLocation_;

        // Disabled params
        const disabledParam = this.getDisabledParam();

        // If not empty, add them to the link
        let iFrameLink = `${this.mapbsUrl}light?`;
        if (disabledParam) {
            iFrameLink += `disabled=${disabledParam}&`;
        }

        // Other params and layers
        const relevantParameter = [
            'lang',
            'baselayer_ref',
            'tree_groups',
            'map_x',
            'map_y',
            'map_zoom',
            'tree_group_layers_',
            'tree_enable_',
            'tree_opacity_',
            'tree_group_opacity_',
            'recenter_icon',
            'rl_features'
        ];

        for (const [key, value] of Object.entries(location.queryData_)) {
            if (relevantParameter.find((x) => key.startsWith(x))) {
                iFrameLink += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
            }
        }
        // Update Iframe and Code
        if (document.getElementById('iframe-light')) {
            document.getElementById('iframe-light').src = iFrameLink;
        }
        if (document.getElementById('iframe-code')) {
            const codeWithoutId = `<iframe title="iframe MapBS" src="${iFrameLink}" frameborder="no" scrolling="no"></iframe>`;
            document.getElementById('iframe-code').value = codeWithoutId;
        }
        return iFrameLink;
    }

    copyToClipboard(target) {
        let toBeCopied = '';
        if (target === 'link') {
            toBeCopied = this.iFrameLink;
        } else {
            const codeWithoutId = `<iframe title="iframe MapBS" src="${this.iFrameLink}" frameborder="no" scrolling="no"></iframe>`;
            toBeCopied = codeWithoutId;
        }
        navigator.clipboard.writeText(toBeCopied);
    }

    updateIFramePreview() {
        // Regenerates iframe link
        this.iFrameLink = this.getIFrameLink();
    }

    setPanels(e) {
        const target = e.target;
        const parent = target.parentNode;
        const grandparent = parent.parentNode;

        // Remove all current selected tabs
        parent.querySelectorAll('[aria-selected="true"]').forEach((t) => {
            t.setAttribute('aria-selected', false);
            t.classList.remove('active');
        });

        // Set this tab as selected
        target.setAttribute('aria-selected', true);
        target.classList.add('active');

        // Hide all tab panels
        grandparent.parentNode
        .querySelectorAll('[role="tabpanel"]')
        .forEach((p) => {
            p.setAttribute('hidden', true);
            p.classList.remove('show');
            p.classList.remove('active');
        });

        // Show the selected panel
        const selectedPanel = grandparent.parentNode.querySelector(
            `#${target.getAttribute('aria-controls')}`
        );
        selectedPanel.removeAttribute('hidden');
        selectedPanel.classList.add('active', 'show');

        if(selectedPanel.id === 'nav-iframe') {
            document.getElementById('ngeo-modal-share').querySelectorAll('.modal-dialog')[0].classList.add('modal-dialog-max-width');
        } else {
            document.getElementById('ngeo-modal-share').querySelectorAll('.modal-dialog')[0].classList.remove('modal-dialog-max-width');
        }
    }
}

myModule.controller('MapbsShareController', MapbsShareController);

export default myModule;
